"use client"

import { useCallback, useRef } from "react"
import Image from "next/image"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { getNonUmbracoUrl } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import { useIsDesktop } from "../../common/bit/MediaQueries"
import GradientChevron from "../../icons/GradientChevron"
import { useScrollScrub } from "../../transitions/useScrollScrub"
import {
  Bleed,
  Card,
  ChevronWrapper,
  Content,
  CtaAnchor,
  CtaLink,
  CtaText,
  Description,
  Media,
  MediaVideo,
  Title,
  TitleGroup,
  TitleRow,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Props {
  data: components["schemas"]["ContentCardPropertiesModel"]
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Detect whether a media picker value points to a video file.
 * Mirrors the logic in packages/ui/layout/Footer/index.tsx (EGDE-36 fix):
 * Umbraco's Delivery API reports the media type alias ("umbracoMediaVideo"),
 * not a bare "video", and frequently omits `extension` on nested media-picker
 * values — so the type is also derived from the file URL.
 */
function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as any
  const mediaType = (media?.mediaType ?? "").toLowerCase()
  const extension = (media?.extension ?? "").toLowerCase()
  const url = (media?.url ?? "").toLowerCase()
  const videoExtensions = ["mp4", "webm", "ogg", "mov"]
  return (
    mediaType.includes("video") ||
    videoExtensions.includes(extension) ||
    videoExtensions.some((ext) => url.endsWith(`.${ext}`))
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function ContentCard({ data }: Props) {
  // EGDE-318: content-editor toggle — parallax only when explicitly enabled.
  // Read via cast (same pattern as LobbyBlock.enableCarousel) so the generated
  // schema needs no hand edit before the CMS property exists everywhere.
  const animate =
    (data as { enableAnimation?: boolean }).enableAnimation === true

  // EGDE-318 scroll-scrubbed parallax: progress is written as a CSS custom
  // property on the card (no per-frame React re-render). 0 = image spread
  // across the whole card / text off-card, 1 = resting design state. The
  // mapping is reversible, so scrolling back up re-enlarges the image.
  //
  // Designer r2 (Raz, 2026-07-14): on desktop the default window finished the
  // shrink while the hero sheet was still rising — the entrance read as "too
  // quick" and the Large full-bleed state was never really seen. The desktop
  // window has to keep the image spread while the card travels up (Zoox-style
  // controlled beat) rather than resolving the moment it peeks over the fold.
  //
  // EGDE-625: r2 bought that beat by pushing the END of the window up to 0.08vh
  // — the copy only finished its run-in once the card had climbed to the top of
  // the screen, so a card sitting low but wholly on screen showed the copy
  // stalled part-way in ("the card is partially visible while it is on the
  // screen"). The window now spans the card's entire entrance instead: it opens
  // as the top edge crosses the viewport floor and closes when the bottom edge
  // clears it. That is a LONGER run than r2's (a full card height of scroll,
  // ~668px at 1920 against 437px), so the beat is if anything more deliberate —
  // it just finishes where the viewer expects it to, with the card fully in view.
  const isDesktop = useIsDesktop()
  const cardRef = useRef<HTMLDivElement>(null)
  useScrollScrub(cardRef, {
    disabled: !animate,
    startVh: isDesktop ? 1 : 0.92,
    endVh: isDesktop ? 0.08 : 0.42,
    completeWhenFullyVisible: true,
    onProgress: useCallback((p: number) => {
      cardRef.current?.style.setProperty("--scrub", String(p))
    }, []),
  })

  // Media (array — take [0]). Mandatory field; bail if absent.
  const relativeUrl = getMediaUrl(data.media)
  const mediaUrl = relativeUrl ? getUmbracoMediaUrl(relativeUrl) : null
  const mediaIsVideo = isVideoMedia(data.media)

  // CTA — render only when both label and link resolve.
  const ctaResolved = resolveLinkHref(data.ctaLink, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  const ctaUrl = ctaResolved?.href ?? null
  const ctaTarget = ctaResolved?.target

  if (!mediaUrl) return null

  const imageOnRight = data.imageOnRight === true

  return (
    /* EGDE-510 point A1: the animated copy now runs in from the screen edge
       rather than from the card edge, so the clip that keeps it off the page's
       horizontal scrollbar has to sit at viewport width — outside Container,
       which is max-width capped and centred. */
    <Bleed>
      <Container paddingMobile>
        <Card
          ref={cardRef}
          data-block="content-card-block"
          $imageOnRight={imageOnRight}
          $animated={animate}
        >
          {/*
           * RTL source order: [Content, Media].
           * In RTL the browser reverses the row, so [Content, Media] renders
           * visually as [Media LEFT, Content RIGHT] — image at x=0, content at
           * the start/right (matches figma-desktop.png).
           */}
          <Content $animated={animate}>
            <TitleGroup>
              <TitleRow>
                {/*
                 * RTL: source [Chevron, Title] renders [Title left, Chevron
                 * right] so the chevron sits at the start of the title.
                 */}
                <ChevronWrapper>
                  <GradientChevron />
                </ChevronWrapper>
                <Title variant="h2">{data.title}</Title>
              </TitleRow>
              {data.description && (
                <Description variant="body1">{data.description}</Description>
              )}
            </TitleGroup>

            {data.ctaLabel &&
              ctaUrl &&
              (ctaResolved?.isInternal ? (
                <CtaLink href={ctaUrl}>
                  <CtaText variant="body1">{data.ctaLabel}</CtaText>
                </CtaLink>
              ) : (
                <CtaAnchor
                  href={ctaUrl}
                  target={ctaTarget}
                  rel={
                    ctaTarget === "_blank" ? "noopener noreferrer" : undefined
                  }
                >
                  <CtaText variant="body1">{data.ctaLabel}</CtaText>
                </CtaAnchor>
              ))}
          </Content>

          <Media $animated={animate}>
            {mediaIsVideo ? (
              <MediaVideo
                src={mediaUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
            ) : (
              <Image
                src={mediaUrl}
                alt={data.title ?? ""}
                fill
                // Animated: the image starts spread across the whole card, so
                // request a container-width source or the Large state is blurry.
                sizes={
                  animate
                    ? "(max-width: 1279px) 343px, 1200px"
                    : "(max-width: 1279px) 343px, 540px"
                }
                style={{ objectFit: "cover" }}
              />
            )}
          </Media>
        </Card>
      </Container>
    </Bleed>
  )
}
